import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import type { TextInput as TextInputType } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin/colors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { AdminTagChipPicker } from './AdminTagChipPicker';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface AnnouncementFormModalProps {
  visible: boolean;
  availableTags: CorkBoardTag[];
  isSaving: boolean;
  onSave: (data: { title: string; body: string; tagIds: number[] }) => void;
  onClose: () => void;
}

export const AnnouncementFormModal: React.FC<AnnouncementFormModalProps> = memo(
  ({ visible, availableTags, isSaving, onSave, onClose }) => {
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [errors, setErrors] = useState<{ title?: string; body?: string }>({});
    const titleInputRef = useRef<TextInputType>(null);

    const categoryTags = useMemo(
      () => availableTags.filter((t) => t.slug !== 'announcements'),
      [availableTags],
    );

    useEffect(() => {
      if (visible) {
        setTitle('');
        setBody('');
        setSelectedTagIds([]);
        setErrors({});
        const timer = setTimeout(() => titleInputRef.current?.focus(), 100);
        return () => clearTimeout(timer);
      }
    }, [visible]);

    const handleToggleTag = useCallback((tagId: number) => {
      setSelectedTagIds((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((id) => id !== tagId);
        }
        return [tagId];
      });
    }, []);

    const validate = useCallback((): boolean => {
      const newErrors: { title?: string; body?: string } = {};
      if (!title.trim()) {
        newErrors.title = 'Title is required';
      } else if (title.trim().length > CORK_BOARD_INPUT_LIMITS.titleMax) {
        newErrors.title = `Title must be ${CORK_BOARD_INPUT_LIMITS.titleMax} characters or fewer`;
      }
      if (!body.trim()) {
        newErrors.body = 'Body is required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [title, body]);

    const handleSave = useCallback(() => {
      if (isSaving || !validate()) return;
      onSave({
        title: title.trim(),
        body: body.trim(),
        tagIds: selectedTagIds,
      });
    }, [isSaving, validate, onSave, title, body, selectedTagIds]);

    const handleOverlayPress = useCallback(() => {
      if (!isSaving) onClose();
    }, [isSaving, onClose]);

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isSaving ? undefined : onClose}>
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <Pressable style={styles.overlayDismiss} onPress={handleOverlayPress}>
            <Pressable style={styles.modalContainer} onPress={(e) => e.stopPropagation()}>
              {/* Header */}
              <View style={styles.header}>
                <Text style={styles.headerTitle}>New Announcement</Text>
                <TouchableOpacity
                  onPress={onClose}
                  disabled={isSaving}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={isSaving ? styles.closeButtonDisabled : undefined}>
                    <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={ADMIN_COLORS.TEXT.SECONDARY} />
                  </View>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent} keyboardShouldPersistTaps="handled">
                {/* Title */}
                <View style={styles.field}>
                  <Text style={styles.label}>Title *</Text>
                  <TextInput
                    ref={titleInputRef}
                    style={[styles.input, errors.title ? styles.inputError : null]}
                    value={title}
                    onChangeText={(text) => {
                      setTitle(text);
                      if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
                    }}
                    placeholder="Announcement title"
                    placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                    maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
                  />
                  {errors.title ? <Text style={styles.errorText}>{errors.title}</Text> : null}
                </View>

                {/* Body */}
                <View style={styles.field}>
                  <Text style={styles.label}>Body *</Text>
                  <TextInput
                    style={[styles.input, styles.bodyInput, errors.body ? styles.inputError : null]}
                    value={body}
                    onChangeText={(text) => {
                      setBody(text);
                      if (errors.body) setErrors((prev) => ({ ...prev, body: undefined }));
                    }}
                    placeholder="Write your announcement..."
                    placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
                    multiline
                    textAlignVertical="top"
                    maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
                  />
                  {errors.body ? <Text style={styles.errorText}>{errors.body}</Text> : null}
                </View>

                {/* Tags */}
                <View style={styles.field}>
                  <Text style={styles.autoTagNote}>
                    Announcements tag will be added automatically
                  </Text>
                  <AdminTagChipPicker
                    tags={categoryTags}
                    selectedTagIds={selectedTagIds}
                    onToggleTag={handleToggleTag}
                    label="Category tag (optional)"
                  />
                </View>
              </ScrollView>

              {/* Footer */}
              <View style={styles.footer}>
                <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={isSaving}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                  onPress={handleSave}
                  disabled={isSaving}
                >
                  <Text style={styles.saveButtonText}>{isSaving ? 'Posting...' : 'Post Announcement'}</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </KeyboardAvoidingView>
      </Modal>
    );
  },
);

AnnouncementFormModal.displayName = 'AnnouncementFormModal';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  overlayDismiss: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    width: '90%',
    maxWidth: 540,
    maxHeight: '85%',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flexShrink: 1,
  },
  bodyContent: {
    padding: 20,
    gap: 20,
  },
  field: {
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  input: {
    backgroundColor: ADMIN_COLORS.INPUT.PRIMARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.INPUT.BORDER,
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  inputError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  bodyInput: {
    minHeight: 120,
    paddingTop: 12,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
  },
  autoTagNote: {
    fontSize: 12,
    fontStyle: 'italic',
    color: ADMIN_COLORS.TEXT_TERTIARY,
    marginBottom: 4,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.BORDER,
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER_SECONDARY,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
  },
});
